import { useAuth } from "@/contexts/AuthContext";
import { EVENT_TYPES } from "@/utils/constants";
import { flushEvents, logEvent, type LogEventMetadata } from "@/utils/helpers/analyticsHelpers";
import { useCallback } from "react";

/**
 * Custom hook for analytics logging
 * Provides easy access to logging functions with automatic user context
 */
export function useAnalytics() {
    const { user } = useAuth();

    const log = useCallback(
        (eventType: string, metadata?: LogEventMetadata) => {
            logEvent(user, eventType, metadata);
        },
        [user]
    );

    const createTracker = useCallback(
        (eventType: string) => (metadata?: LogEventMetadata) => {
            log(eventType, metadata);
        },
        [log]
    );

    const createListingTracker = useCallback(
        (eventType: string) => (listingId: string, vendor?: string, metadata?: LogEventMetadata) => {
            log(eventType, {
                listing_id: listingId,
                vendor,
                ...metadata,
            });
        },
        [log]
    );

    const flush = useCallback(async () => {
        await flushEvents();
    }, []);

    return {
        logEvent: log,
        flush,
        trackLogin: createTracker(EVENT_TYPES.LOGIN),
        trackLogout: createTracker(EVENT_TYPES.LOGOUT),
        trackRegistration: createTracker(EVENT_TYPES.REGISTER),
        trackUpdateUser: createTracker(EVENT_TYPES.UPDATE_USER),
        trackListingCardClick: createListingTracker(EVENT_TYPES.CLICK_LISTING_CARD),
        trackGoToVendorListing: createListingTracker(EVENT_TYPES.CLICK_GO_TO_VENDOR_LISTING),
        trackFavoriteAdded: createListingTracker(EVENT_TYPES.FAVORITE_ADDED),
        trackFavoriteRemoved: createListingTracker(EVENT_TYPES.FAVORITE_REMOVED),
        trackNotifyAdded: createListingTracker(EVENT_TYPES.NOTIFY_ADDED),
        trackNotifyRemoved: createListingTracker(EVENT_TYPES.NOTIFY_REMOVED),
        trackSearch: useCallback(
            (query: string, filters?: Record<string, unknown>, metadata?: LogEventMetadata) => {
                log(EVENT_TYPES.SEARCH, {
                    query,
                    filters,
                    ...metadata,
                });
            },
            [log]
        ),
    };
}